/**
 * Pure presentational report primitives shared by the voice and SMS campaign
 * report dashboards. Every component here is data-agnostic — it takes plain
 * props (numbers, labels, semantic colors) and renders. No domain types, no
 * data hooks, no i18n lookups (callers pass already-translated strings).
 *
 * Lifted verbatim from `pages/voice/campaign-report.tsx` so the two reports
 * stay pixel-identical. Class names and inline styles are intentionally
 * unchanged from the original voice implementation.
 */

import type { JSX, ReactNode } from 'react';

import { Icon, type IconName } from '@/components/icons';
import { Card } from '@/components/ui';

import { chipBg, fmtPct, pctInt, semVar, type ReportBucket, type Sem } from './util';

// ---------------------------------------------------------------------------
// Progress ring — completion percentage with a labeled center.
// ---------------------------------------------------------------------------

export function ProgressRing({
  pct,
  centerLabel,
}: {
  pct: number;
  centerLabel: string;
}): JSX.Element {
  const size = 120;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  const dash = (clamped / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          style={{ stroke: 'var(--surface-3)' }}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          style={{ stroke: 'var(--success)' }}
          strokeDasharray={`${dash} ${c - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="display text-[24px] font-semibold text-text tabular-nums leading-none">
          {pctInt(clamped)}
        </span>
        <span className="text-[10px] uppercase tracking-[0.06em] text-text-4 mt-1">
          {centerLabel}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero figure — a small labeled figure with a semantic swatch (ring sidebar).
// ---------------------------------------------------------------------------

export function HeroFigure({
  label,
  value,
  sem,
}: {
  label: string;
  value: string;
  sem: Sem;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
        style={{ backgroundColor: semVar[sem] }}
        aria-hidden
      />
      <span className="text-[12px] text-text-3">{label}</span>
      <span className="display text-[16px] font-semibold text-text tabular-nums ml-auto">
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero stat — the gradient hero card with icon, big value, optional pill + bar.
// ---------------------------------------------------------------------------

export function HeroStat({
  sem,
  icon,
  label,
  value,
  sub,
  pill,
  pct,
}: {
  sem: Sem;
  icon: JSX.Element;
  label: string;
  value: string;
  sub: string;
  pill?: string;
  pct?: number;
}): JSX.Element {
  const color = semVar[sem];
  return (
    <Card
      className="p-4 flex flex-col justify-center overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${chipBg(sem)}, var(--surface) 62%)`,
        borderColor: `var(--${sem}-bd)`,
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 text-white"
          style={{ backgroundColor: color }}
          aria-hidden
        >
          {icon}
        </span>
        <span className="text-[10px] uppercase tracking-[0.06em] text-text-4">{label}</span>
        {pill && (
          <span
            className="text-[9px] font-semibold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded-pill border"
            style={{ color, backgroundColor: chipBg(sem), borderColor: `var(--${sem}-bd)` }}
          >
            {pill}
          </span>
        )}
      </div>
      <div
        className="display text-[36px] font-semibold tabular-nums leading-none mt-2.5"
        style={{ color }}
      >
        {value}
      </div>
      <div className="text-[11px] text-text-4 mt-1.5 tabular-nums truncate">{sub}</div>
      {pct !== undefined && (
        <div
          className="h-[7px] rounded-pill mt-3 overflow-hidden"
          style={{ backgroundColor: 'var(--surface-3)' }}
        >
          <div
            className="h-full rounded-pill"
            style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: color }}
          />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// KPI chip — compact icon + label + value tile.
// ---------------------------------------------------------------------------

export function KpiChip({
  sem,
  icon,
  label,
  value,
}: {
  sem: Sem;
  icon: IconName;
  label: string;
  value: string;
}): JSX.Element {
  const IconCmp = Icon[icon];
  return (
    <Card className="p-4 flex items-center gap-3">
      <span
        className="inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0"
        style={{ color: semVar[sem], backgroundColor: chipBg(sem) }}
        aria-hidden
      >
        <IconCmp width={16} height={16} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.06em] text-text-4 truncate">{label}</div>
        <div className="display text-[20px] font-semibold text-text tabular-nums leading-tight">
          {value}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section title — uppercase label with a leading icon and optional hint pill.
// ---------------------------------------------------------------------------

export function SectionTitle({
  children,
  icon = 'chart',
  hint,
}: {
  children: ReactNode;
  icon?: IconName;
  hint?: string;
}): JSX.Element {
  const IconCmp = Icon[icon];
  return (
    <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-semibold mb-3 flex items-center gap-1.5">
      <IconCmp width={13} height={13} className="text-text-4" />
      {children}
      {hint ? (
        <span className="ml-1 normal-case tracking-normal font-medium bg-surface-2 text-text-4 rounded px-1.5 py-0.5 text-[10px]">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut — call/message-outcome ring with a labeled center.
// ---------------------------------------------------------------------------

export function Donut({
  segments,
  total,
  centerValue,
  centerLabel,
}: {
  segments: { key: string; count: number; sem: Sem }[];
  total: number;
  centerValue: string;
  centerLabel: string;
}): JSX.Element {
  const size = 120;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          style={{ stroke: 'var(--surface-3)' }}
        />
        {total > 0 &&
          segments.map((s) => {
            if (s.count === 0) return null;
            const len = (s.count / total) * c;
            const rot = (acc / total) * 360 - 90;
            acc += s.count;
            return (
              <circle
                key={s.key}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                strokeWidth={stroke}
                style={{ stroke: semVar[s.sem] }}
                strokeDasharray={`${len} ${c - len}`}
                transform={`rotate(${rot} ${size / 2} ${size / 2})`}
              />
            );
          })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="display text-[22px] font-semibold text-text tabular-nums leading-none">
          {centerValue}
        </span>
        <span className="text-[10px] uppercase tracking-[0.06em] text-text-4 mt-1">
          {centerLabel}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut legend — the per-segment "swatch · label · count (pct)" list that
// sits beside a Donut.
// ---------------------------------------------------------------------------

export function DonutLegend({
  segments,
  total,
}: {
  segments: ReportBucket[];
  total: number;
}): JSX.Element {
  return (
    <div className="min-w-0 space-y-2 flex-1">
      {segments.map((s) => (
        <div key={s.key} className="flex items-center gap-2 text-[12px]">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: semVar[s.sem] }}
            aria-hidden
          />
          <span className="text-text-2 truncate">{s.label}</span>
          <span className="text-text-4 tabular-nums ml-auto">
            {s.count.toLocaleString()} ({fmtPct(total > 0 ? (s.count / total) * 100 : 0)})
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status stacked bar — a stacked completion bar + per-status grid + a
// "reconciles to N" line. Generic over any set of labeled, colored buckets.
// ---------------------------------------------------------------------------

export function StatusStackedBar({
  buckets,
  total,
  reconcileLabel,
}: {
  buckets: ReportBucket[];
  total: number;
  reconcileLabel: string;
}): JSX.Element {
  const nonZero = buckets.filter((x) => x.count > 0);

  return (
    <div className="space-y-4">
      <div className="flex h-4 rounded-full overflow-hidden bg-surface-2">
        {nonZero.map((x) => (
          <div
            key={x.key}
            style={{
              width: `${total > 0 ? (x.count / total) * 100 : 0}%`,
              backgroundColor: semVar[x.sem],
            }}
            title={`${x.label}: ${x.count.toLocaleString()}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2.5">
        {nonZero.map((x) => (
          <div key={x.key} className="flex items-start gap-2 min-w-0">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm shrink-0 mt-1"
              style={{ backgroundColor: semVar[x.sem] }}
              aria-hidden
            />
            <div className="min-w-0">
              <div className="text-[12px] text-text-2 truncate">{x.label}</div>
              <div className="text-[13px] text-text tabular-nums">
                {x.count.toLocaleString()}{' '}
                <span className="text-text-4">
                  ({fmtPct(total > 0 ? (x.count / total) * 100 : 0)})
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-text-4">{reconcileLabel}</p>
    </div>
  );
}
